import React, { useRef, useState } from 'react';
import {
  Box, Button, Card, CardContent, Divider, Grid, IconButton, Stack, TextField, Typography, ToggleButton,
  ToggleButtonGroup, Tooltip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { domToPng } from 'modern-screenshot';
import { BarChart } from '@mui/x-charts/BarChart';
import {
  KpiSummary, VolumePoint, WeekdayPoint, HeatmapCell, BreakdownItem,
  DurationBucket, Granularity,
} from '../../services/analyticsService';

// Download a DOM node as a PNG, client-side. modern-screenshot renders SVG
// (the @mui/x-charts charts) and fonts correctly — html-to-image lost the bar
// fill (→ black) and label styling (→ misaligned). For a horizontally-scrolling
// node (the heatmap grid) capture the full scroll size, not just the visible box.
const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

// Nodes marked with this are dropped from the capture: they are page controls
// (download button, granularity toggle) that mean nothing inside a still image.
// An explicit attribute rather than sniffing the element tree, so it survives a
// refactor of the MUI components underneath.
const SKIP_ATTR = { 'data-capture': 'skip' };
const notAControl = (n: Node) =>
  !(n instanceof Element) || n.getAttribute('data-capture') !== 'skip';

async function downloadPng(node: HTMLElement | null, name: string) {
  if (!node) return;
  try {
    const opts: {
      scale: number; backgroundColor: string; width?: number; height?: number;
      filter: (n: Node) => boolean;
    } = { scale: 2, backgroundColor: '#ffffff', filter: notAControl };
    if (node.scrollWidth > node.clientWidth || node.scrollHeight > node.clientHeight) {
      opts.width = node.scrollWidth;
      opts.height = node.scrollHeight;
    }
    const dataUrl = await domToPng(node, opts);
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `${slug(name) || 'grafico'}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch {
    /* soft-fail: a capture error must not break the page */
  }
}

// The one download control on every chart and on the summary. A labelled button
// rather than a bare icon: the icon alone did not say WHAT it downloads, and the
// page now has three download actions side by side (chart, report, call detail).
// Busy while the capture runs — it takes a moment, and a second click used to
// start a second one. Marked as a page control, so it never appears in the image.
const DownloadButton: React.FC<{ node: () => HTMLElement | null; name: string }> =
  ({ node, name }) => {
    const [busy, setBusy] = useState(false);
    return (
      <Button
        variant="contained" size="small"
        startIcon={<DownloadIcon fontSize="small" />}
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          try { await downloadPng(node(), name); } finally { setBusy(false); }
        }}
        sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
        {...SKIP_ATTR}
      >
        {busy ? 'Scarico…' : 'Scarica Grafico'}
      </Button>
    );
  };

const CHART_H = 300;
// #1976d2 = theme.palette.primary.main (the top-bar blue); one source keeps
// every chart and the heatmap uniform with the app chrome.
const CHART_RGB = '25, 118, 210';
const CHART_COLOR = `rgb(${CHART_RGB})`;
// White in-bar value labels (SVG text → styled via `fill`), readable on the blue bars.
const BAR_LABEL_SX = { '& .MuiBarLabel-root': { fill: '#fff', fontWeight: 600 } };
// The value axis has to be formatted explicitly: x-charts ticks do not go through
// fmtInt and its default is not locale-aware, so an axis reads "4,000" next to a
// cell reading "4.000". Kept as one object so no chart can drift from the others.
// tickMinStep:1 keeps the scale on whole numbers. Without it, a chart whose
// counts are small gets ticks at 0,5 / 1,5 — and half a call does not exist, so
// the axis would be offering a value the data can never take.
const VALUE_AXIS = {
  valueFormatter: (v: number | null) => (v == null ? '' : fmtInt(v)),
  tickMinStep: 1,
};
// Single-series legend ("Chiamate") is redundant now that every chart carries an
// explicit title, and it costs vertical space. v7 API; v8 renames this to hideLegend.
const NO_LEGEND = { legend: { hidden: true } } as const;

// A number only stays inside a vertical bar when the bar can actually hold it.
// x-charts v7 centres bar labels and gives no way to move them out, so a short
// bar used to push its white label onto the white background — the smallest
// counts, the ones you most need to read, were the ones that disappeared. Where
// the bar cannot hold the figure the honest alternative is no figure: the value
// axis carries whole-number ticks, the tooltip carries the exact count, and the
// export carries the table. A number you cannot read is worse than none, because
// it looks like information.
//
// The geometry arrives in the SECOND argument (`context.bar`), not on the item —
// the v7 docs example says otherwise and the types are right.
const BAR_LABEL_MIN_H = 18;   // room for the glyphs, not just for the line box
const BAR_LABEL_MIN_W = 34;   // "13.250" at this size, plus breathing room
const barLabelIfItFits = (
  item: { value: number | null },
  context: { bar: { height: number; width: number } },
) => (
  !item.value                       // null, and zero: an empty bar labelled "0"
                                    // is noise where the missing bar already says it
  || context.bar.height < BAR_LABEL_MIN_H
  || context.bar.width < BAR_LABEL_MIN_W
    ? null
    : fmtInt(item.value)
);
// Italian locale formatting: "." thousands separator, "," decimals.
// useGrouping is required, not decoration: it-IT declares minimumGroupingDigits:2,
// i.e. "group only from five digits up", so the plain locale renders 13.250 and
// 2043 side by side in the same column. `true` is normalised to "always" by
// ECMA-402; the literal 'always' is the same thing but not in this TS lib yet.
const fmtInt = (n: number) => n.toLocaleString('it-IT', { useGrouping: true });
// Satisfaction is the only figure on the page that is a score, not a count:
// one fixed decimal plus the full scale, so 7,1 cannot be read as a count.
const fmtSat = (n: number) =>
  `${n.toLocaleString('it-IT', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}/10`;
const pct = (x: number) =>
  `${(x * 100).toLocaleString('it-IT', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
// The one figure on the page that does NOT follow the Italian convention: a dot
// for the thousands AND for the decimals, by request. Deliberate, so nobody
// "fixes" it back into line with the counts around it.
//
// The same sign doing two jobs stays readable only because the decimals are
// ALWAYS two: the last dot is always the cents. Keep the fixed two decimals —
// drop them and "€ 1.500" stops meaning one thousand five hundred.
const eur = (x: number) =>
  `€ ${x.toLocaleString('it-IT', {
    minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: true,
  }).replace(/,(\d{2})$/, '.$1')}`;

//Each chart says what it is showing, between its title and the caption. Per CHART
// and not per report: "Chiamate per ufficio/servizio" draws two of them and
// "Chiamate per periodo" three, each grouping the same calls a different way, so
// one sentence per report would be wrong on at least one chart. Client wording,
// kept verbatim.
const MOSTRA = 'Il report mostra il totale delle chiamate ricevute da GiulIA nel periodo selezionato';
export const DESC = {
  kpi: `${MOSTRA}, con il dettaglio delle interazioni gestite in autonomia o trasferite, del numero di prenotazioni effettuate e del costo complessivo`,
  volume: `${MOSTRA}, raggruppate per mese, settimana o giorno`,
  brevi: 'Il report mostra il totale delle chiamate brevi non trasferite (sotto i 25 secondi) ricevute da GiulIA nel periodo selezionato, raggruppate per mese, settimana o giorno',
  motivo: `${MOSTRA}, raggruppate per principale motivo di contatto`,
  ufficio: `${MOSTRA}, raggruppate per ufficio/categoria di competenza`,
  servizio: `${MOSTRA}, raggruppate per servizio richiesto`,
  weekday: `${MOSTRA}, raggruppate per giorno della settimana`,
  durata: `${MOSTRA}, raggruppate per durata della chiamata`,
  heatmap: `${MOSTRA}, raggruppate per fascia oraria e giorno della settimana`,
} as const;

// Default titles, in the same per-chart vocabulary as DESC. Together they are the
// text a chart shows when the active report has not overridden it.
export const TITLES = {
  kpi: 'Riepilogo chiamate',
  volume: 'Numero di chiamate ricevute da GiulIA',
  brevi: 'Numero chiamate brevi non trasferite ricevute da GiulIA',
  motivo: 'Numero chiamate ricevute da GiulIA per principale motivo di contatto',
  ufficio: 'Numero chiamate ricevute da GiulIA per ufficio/categoria',
  servizio: 'Numero chiamate ricevute da GiulIA per dettaglio servizi',
  weekday: 'Numero chiamate ricevute da GiulIA per giorno della settimana',
  durata: 'Numero chiamate ricevute da GiulIA per durata',
  heatmap: 'Numero chiamate ricevute da GiulIA per giorno e ora',
} as const;

// The charts whose texts a report can override. "brevi" is not one: it is the
// volume chart under another default, and the override is keyed on the chart.
export type ChartKey =
  'kpi' | 'volume' | 'motivo' | 'ufficio' | 'servizio' | 'weekday' | 'durata' | 'heatmap';
export const CHART_KEYS: ChartKey[] =
  ['kpi', 'volume', 'motivo', 'ufficio', 'servizio', 'weekday', 'durata', 'heatmap'];

// Hard caps, enforced in the editor and again when a stored preset is read: the
// text lands in a card header and in an xlsx cell, and the preset blob has a size
// limit on the server.
export const TITLE_MAX = 120;
export const DESCRIPTION_MAX = 400;

// Present only when the viewer may edit (an admin). A chart without it renders
// exactly as before — no pencil, nothing to click.
export interface TextEdit {
  customised: boolean;
  onApply: (title: string, description: string) => void;
  onReset: () => void;
}

// -- Selected period, rendered for the capture -------------------------------
// Lives inside the captured node on purpose: a period shown only in the filter
// panel would be correct on screen and missing from every downloaded image —
// i.e. absent exactly where the chart loses its context.
export interface PeriodoProps { period?: string; from?: string; to?: string }

const D = (s: string) => new Date(`${s}T00:00:00`);
const long = (s: string) =>
  D(s).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });
const pad = (n: number) => String(n).padStart(2, '0');
const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// Mirrors _apply_period in call_analytics.py, for DISPLAY only — the backend
// still does the filtering. It exists because a caption reading "questo_mese"
// (or worse, "Tutto il periodo") tells the reader of a downloaded PNG nothing:
// the dates do. Periods the backend leaves open-ended are shown up to today,
// which is the window that actually holds data.
export function resolvePeriod(period?: string): { from?: string; to?: string } {
  const now = new Date();
  const shifted = (days: number) => {
    const d = new Date(now);
    d.setDate(d.getDate() + days);
    return iso(d);
  };
  switch (period) {
    case 'oggi': return { from: iso(now), to: iso(now) };
    case 'ieri': return { from: shifted(-1), to: shifted(-1) };
    case 'ultimi_7_giorni': return { from: shifted(-6), to: iso(now) };
    case 'questo_mese': return { from: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-01`, to: iso(now) };
    default: return {};   // 'sempre' or unset → genuinely unbounded
  }
}

// Volume buckets that suit the window's length: up to a week by day, up to a
// month by week, beyond that by month. "Up to" is inclusive on purpose — a whole
// month by month is a single bar, and a week by week one or two. A window with
// no end ("Sempre") is the longest there is.
export function autoGranularity(p: PeriodoProps): Granularity {
  const { from, to } = (p.from || p.to) ? p : resolvePeriod(p.period);
  if (!from || !to) return 'month';
  // Rounded: a DST change makes one day 23 or 25 hours long.
  const days = Math.round((D(to).getTime() - D(from).getTime()) / 86_400_000) + 1;
  return days <= 7 ? 'day' : days <= 31 ? 'week' : 'month';
}

export function periodoText(p: PeriodoProps): string {
  // Explicit dates win, exactly as they do in the backend filter contract.
  const { from, to } = (p.from || p.to) ? p : resolvePeriod(p.period);
  if (from && to) {
    if (from === to) return long(from);   // 'oggi' / 'ieri' collapse to one date
    // Same month and year → "1 – 30 settembre 2026" instead of repeating both.
    const a = D(from);
    const b = D(to);
    if (a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()) {
      return `${a.getDate()} – ${long(to)}`;
    }
    return `${long(from)} – ${long(to)}`;
  }
  if (from) return `Dal ${long(from)}`;
  if (to) return `Fino al ${long(to)}`;
  return 'Tutto il periodo';
}

// Title, what the chart shows, and its scope: one block, three kinds of text, and
// each gets its own voice. Without that they read as a single grey paragraph —
// the title at body weight, the description straight under it in grey, and the
// "comune · period" line indistinguishable from a third line of the sentence.
//
//   title        the anchor; weight carries it, not size
//   description  prose, kept to a readable measure
//   scope        metadata — where and when — marked with icons so it cannot be
//                mistaken for more prose, and separated by space, not by grey
//
// All three live inside the captured node, so a downloaded image keeps them.
const MetaItem: React.FC<{ icon: React.ReactNode; children: React.ReactNode; strong?: boolean }> =
  ({ icon, children, strong }) => (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
      {icon}
      <Typography
        variant="caption"
        sx={{ lineHeight: 1.4, ...(strong && { color: 'text.primary', fontWeight: 500 }) }}
      >
        {children}
      </Typography>
    </Box>
  );

const CardHeading: React.FC<{
  title: string; description?: string; comune?: string; periodo?: PeriodoProps;
  size?: 'card' | 'section'; edit?: TextEdit;
}> = ({ title, description, comune, periodo, size = 'card', edit }) => {
  const [editing, setEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState(title);
  const [draftDesc, setDraftDesc] = useState(description ?? '');

  const open = () => { setDraftTitle(title); setDraftDesc(description ?? ''); setEditing(true); };
  const apply = () => {
    edit?.onApply(draftTitle.trim(), draftDesc.trim());
    setEditing(false);
  };

  const meta = periodo && (
    <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', columnGap: 2, rowGap: 0.5 }}>
      {comune && (
        <MetaItem strong icon={<PlaceOutlinedIcon sx={{ fontSize: 15 }} />}>{comune}</MetaItem>
      )}
      <MetaItem icon={<CalendarTodayOutlinedIcon sx={{ fontSize: 13 }} />}>
        {periodoText(periodo)}
      </MetaItem>
    </Box>
  );

  return (
    // Basis low enough that a lone download button keeps its place on the title
    // row down to phone width; the volume chart's 220px granularity toggle still
    // wraps under the heading when there is no room, which is where it belongs.
    <Box sx={{ flex: '1 1 12rem', minWidth: 0 }}>
      {editing ? (
        // The editor takes the heading's own place, so the admin edits the text
        // where it will be read — and it is a page control, so a capture taken
        // mid-edit shows the fields' values, never the fields themselves.
        <Stack spacing={1.5} sx={{ maxWidth: '62ch' }} {...SKIP_ATTR}>
          <TextField
            label="Titolo del grafico" size="small" fullWidth autoFocus
            value={draftTitle}
            onChange={e => setDraftTitle(e.target.value.slice(0, TITLE_MAX))}
            onKeyDown={e => { if (e.key === 'Escape') setEditing(false); }}
            helperText={`${draftTitle.length}/${TITLE_MAX}`}
            error={!draftTitle.trim()}
          />
          <TextField
            label="Descrizione" size="small" fullWidth multiline minRows={2}
            value={draftDesc}
            onChange={e => setDraftDesc(e.target.value.slice(0, DESCRIPTION_MAX))}
            onKeyDown={e => { if (e.key === 'Escape') setEditing(false); }}
            helperText={`${draftDesc.length}/${DESCRIPTION_MAX} · lascia vuota per non mostrarla`}
          />
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Button size="small" variant="contained" onClick={apply} disabled={!draftTitle.trim()}>
              Applica
            </Button>
            <Button size="small" onClick={() => setEditing(false)}>Annulla</Button>
            {edit?.customised && (
              <Button size="small" color="inherit"
                onClick={() => { edit.onReset(); setEditing(false); }}>
                Ripristina testo predefinito
              </Button>
            )}
          </Stack>
          {/* Named so it cannot be mistaken for saving: Applica changes the page,
              only saving the report keeps it. */}
          <Typography variant="caption" color="text.secondary">
            Le modifiche restano solo se salvi il report.
          </Typography>
        </Stack>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
            <Typography
              variant={size === 'section' ? 'h6' : 'subtitle1'}
              sx={{ fontWeight: 600, lineHeight: 1.3, textWrap: 'balance' }}
            >
              {title}
            </Typography>
            {edit && (
              <IconButton
                size="small" aria-label="Modifica titolo e descrizione"
                title="Modifica titolo e descrizione"
                onClick={open} sx={{ mt: '-3px', color: 'text.secondary' }}
                {...SKIP_ATTR}
              >
                <EditOutlinedIcon sx={{ fontSize: 17 }} />
              </IconButton>
            )}
          </Box>
          {description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.75, maxWidth: '62ch', lineHeight: 1.55, textWrap: 'pretty' }}
            >
              {description}
            </Typography>
          )}
        </>
      )}
      {meta}
    </Box>
  );
};

// `level` is not decoration, it is the arithmetic: total splits into managed +
// brief, and managed splits into autonomous + transferred. The deepest two
// percentages are shares of MANAGED calls, not of the total, and a flat list
// invites reading every row against the same base.
const Row: React.FC<{ label: string; value: React.ReactNode; level?: 0 | 1 | 2 }> =
  ({ label, value, level = 0 }) => (
    <Box display="flex" justifyContent="space-between" sx={{ mb: 0.75, pl: level * 2 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2"><strong>{value}</strong></Typography>
    </Box>
  );

// -- KPI cards (reconciled) -------------------------------------------------
export const KPICards: React.FC<{
  kpi: KpiSummary; periodo?: PeriodoProps; title: string;
  description: string; comune: string; edit?: TextEdit;
}> = ({ kpi, periodo, title, description, comune, edit }) => {
  const o = kpi.operativi;
  // Capture wraps title + period + cards, not the bare Grid: the same reason the
  // chart cards capture their header.
  const ref = useRef<HTMLDivElement>(null);
  return (
    // Section heading for the three cards below it, in the same component the chart
    // cards use, one size up. The download sits in the heading row instead of
    // being pinned to a corner where it read as belonging to nothing.
    // Padding inside the captured node, cancelled by an equal negative margin so
    // the page layout is unchanged: without it the cards' shadows fall outside
    // the node and the image cuts them off at the bottom and sides.
    <Box sx={{ p: 1.5, m: -1.5, mb: -0.5 }} ref={ref}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 1.5, mb: 2.5 }}>
        <CardHeading size="section" title={title} description={description}
          comune={comune} periodo={periodo} edit={edit} />
        <Box sx={{ ml: 'auto' }}>
          <DownloadButton node={() => ref.current} name={title} />
        </Box>
      </Box>
      <Grid container spacing={3}>
      <Grid item xs={12} md={4}>
        <Card elevation={3} sx={{ height: '100%' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>📊 Chiamate</Typography>
            <Divider sx={{ mb: 2 }} />
            <Row label="Chiamate totali" value={fmtInt(o.chiamate)} />
            <Row level={1} label="Di cui gestite" value={`${fmtInt(o.gestite)} (${pct(o.gestite_pct)})`} />
            <Row level={2} label="In autonomia da GiulIA" value={`${fmtInt(o.autonome)} (${pct(o.autonome_pct)})`} />
            <Row level={2} label="Trasferite ad ufficio" value={`${fmtInt(o.trasferite)} (${pct(o.trasferite_pct)})`} />
            <Row level={1} label="Chiamate brevi non trasferite" value={`${fmtInt(o.troppo_brevi)} (${pct(o.troppo_brevi_pct)})`} />
            <Row label="Soddisfazione media" value={o.soddisfazione_media != null ? fmtSat(o.soddisfazione_media) : '—'} />
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={4}>
        <Card elevation={3} sx={{ height: '100%' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>📅 Prenotazioni</Typography>
            <Divider sx={{ mb: 2 }} />
            <Row label="Effettuate" value={fmtInt(kpi.prenotazioni.completate)} />
            <Row label="Non effettuate per problemi nella prenotazione"
              value={fmtInt(kpi.prenotazioni.non_effettuate)} />
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={4}>
        <Card elevation={3} sx={{ height: '100%' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>💶 Costi</Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="h4">{eur(kpi.costi.complessivo_eur)}</Typography>
            {/* Visible text, deliberately not a tooltip: the card gets captured
                into a PNG, and a caveat that only exists on hover is absent
                exactly when the figure starts circulating without the page. */}
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Solo costi fatturati da Vapi. Eventuali modelli con abbonamento non Vapi
              non sono contabilizzati.
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      </Grid>
    </Box>
  );
};

// Sum of what a chart draws — the same figure as the "Totale" row of its sheet in
// the export, so the reader no longer adds the bars up by hand.
const sumCounts = (data: { count: number }[]) => data.reduce((s, d) => s + d.count, 0);

const ChartCard: React.FC<{
  title: string; description?: string; comune?: string; edit?: TextEdit;
  periodo?: PeriodoProps; action?: React.ReactNode; total?: number; children: React.ReactNode;
}> = ({ title, description, comune, edit, periodo, action, total, children }) => {
    // The whole card is captured, header included: a downloaded chart has to stay
    // self-describing once it is out of the dashboard. The controls are excluded
    // by the SKIP_ATTR filter instead, so title and period survive and the
    // buttons do not.
    const cardRef = useRef<HTMLDivElement>(null);
    return (
      <Card elevation={2} sx={{ height: '100%' }} ref={cardRef}>
        <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 1.5 }}>
            <CardHeading title={title} description={description} comune={comune}
              periodo={periodo} edit={edit} />
            <Box sx={{
              display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1,
              flexShrink: 0, ml: 'auto',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} {...SKIP_ATTR}>
                {action}
                <DownloadButton node={() => cardRef.current} name={title} />
              </Box>
              {/* Outside the skipped controls, so the downloaded image keeps it. */}
              {total !== undefined && (
                <Typography variant="body2" color="text.secondary">
                  Totale chiamate:{' '}
                  <Box component="strong" sx={{ color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
                    {fmtInt(total)}
                  </Box>
                </Typography>
              )}
            </Box>
          </Box>
          {/* Same title-then-rule grammar as the KPI cards on this page, which the
              chart cards lacked: the header ends here, the data begins. */}
          <Divider sx={{ mt: 2, mb: 2.5 }} />
          {children}
        </CardContent>
      </Card>
    );
  };

// -- Few-column distributions: the count sits ABOVE its bar -----------------
//
// Not a BarChart, for the same reason the taxonomy breakdown is not one: x-charts
// v7 centres the count inside the bar and offers no way to move it, so a low bar
// pushed its white label onto the white background. On the volume chart, with a
// month of daily bars, the honest answer is to drop the labels and read the axis.
// Here it is not: these have five to seven columns, Saturday and Sunday are low
// BECAUSE that is the finding, and those are precisely the numbers worth reading.
//
// With so few columns the count fits above each bar with room to spare, which
// keeps the vertical shape — the weekday hump, the duration peak — while making
// every figure legible regardless of how short its bar is. `bottom: <pct>%` pins
// the number's baseline to the bar's top edge, so it tracks the bar exactly
// instead of floating at a fixed height.
const COLUMN_H = 220;

const DistributionColumns: React.FC<{ data: { label: string; count: number }[] }> =
  ({ data }) => {
    const max = data.reduce((m, d) => Math.max(m, d.count), 0);
    return (
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, pt: 3 }}>
        {data.map(d => {
          const pctOfMax = max ? (d.count / max) * 100 : 0;
          return (
            <Box key={d.label} sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ position: 'relative', height: COLUMN_H, display: 'flex', alignItems: 'flex-end' }}>
                <Box sx={{
                  width: '100%',
                  height: `${pctOfMax}%`,
                  bgcolor: CHART_COLOR,
                  borderRadius: '2px 2px 0 0',
                }} />
                {d.count > 0 && (
                  <Typography
                    variant="body2"
                    sx={{
                      position: 'absolute', bottom: `${pctOfMax}%`, left: 0, right: 0,
                      textAlign: 'center', fontWeight: 600,
                      fontVariantNumeric: 'tabular-nums', pb: 0.5,
                    }}
                  >
                    {fmtInt(d.count)}
                  </Typography>
                )}
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                title={d.label}
                sx={{
                  display: 'block', textAlign: 'center', pt: 0.75,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}
              >
                {d.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  };

// -- Volume over time (+ granularity toggle) --------------------------------
//
// Two renderings, chosen by how many columns there are, because the honest answer
// differs. Up to COLUMNS_WITH_LABELS the counts sit above the bars like the other
// distributions — and that covers the shapes people actually open: a month by
// week is four or five columns, a year by month is twelve. Past that (a month by
// DAY is thirty) the counts would collide with each other and the dates would
// turn to mush, so it falls back to the chart, which thins its axis ticks and
// drops the labels that do not fit. One rendering for both would have to be wrong
// in one of the two cases.
const COLUMNS_WITH_LABELS = 16;

export const VolumeChart: React.FC<{
  data: VolumePoint[];
  granularity: Granularity;
  onGranularityChange: (g: Granularity) => void;
  title: string;
  description: string;
  comune: string;
  periodo?: PeriodoProps;
  edit?: TextEdit;
}> = ({
  data, granularity, onGranularityChange, title, description, comune, periodo, edit,
}) => (
  <ChartCard
    title={title}
    description={description}
    comune={comune}
    periodo={periodo}
    edit={edit}
    total={sumCounts(data)}
    action={
      <ToggleButtonGroup
        size="small" exclusive value={granularity}
        onChange={(_e, v) => v && onGranularityChange(v)}
      >
        <ToggleButton value="day">Giorno</ToggleButton>
        <ToggleButton value="week">Settimana</ToggleButton>
        <ToggleButton value="month">Mese</ToggleButton>
      </ToggleButtonGroup>
    }
  >
    {data.length <= COLUMNS_WITH_LABELS ? (
      <DistributionColumns data={data.map(d => ({ label: d.bucket, count: d.count }))} />
    ) : (
      <BarChart
        height={CHART_H}
        barLabel={barLabelIfItFits}
        sx={BAR_LABEL_SX}
        slotProps={NO_LEGEND}
        xAxis={[{ scaleType: 'band', data: data.map(d => d.bucket) }]}
        yAxis={[VALUE_AXIS]}
        series={[{ data: data.map(d => d.count), label: 'Chiamate', color: CHART_COLOR }]}
      />
    )}
  </ChartCard>
);

// -- Weekday distribution ---------------------------------------------------
export const WeekdayChart: React.FC<{
  data: WeekdayPoint[]; periodo?: PeriodoProps; comune: string; title: string; description: string; edit?: TextEdit;
}> = ({ data, periodo, comune, title, description, edit }) => (
  <ChartCard title={title} description={description} comune={comune} periodo={periodo} edit={edit}
    total={sumCounts(data)}>
    <DistributionColumns data={data} />
  </ChartCard>
);

// -- Duration distribution --------------------------------------------------
export const DurationChart: React.FC<{
  data: DurationBucket[]; periodo?: PeriodoProps; comune: string; title: string; description: string; edit?: TextEdit;
}> = ({ data, periodo, comune, title, description, edit }) => (
  <ChartCard title={title} description={description} comune={comune} periodo={periodo} edit={edit}
    total={sumCounts(data)}>
    <DistributionColumns data={data} />
  </ChartCard>
);

// -- Taxonomy breakdown: name, proportional bar, value in its own column ----
//
// Not a BarChart. The count used to sit INSIDE the bar in white, which made its
// legibility depend on the bar's length — i.e. on the very quantity it was there
// to report: the smallest values, the ones you most need to read, were the ones
// that fell off the end onto white. x-charts v7 centres bar labels itself and
// exposes no way to move them, so the fix is to take the number out of the bar
// rather than to nudge it.
//
// A right-aligned column reads better besides: the eye scans one line of figures
// instead of hunting them at a different x on every row. Tabular numerals keep
// the digits in register — proportional ones make 1.111 narrower than 8.888 and
// the column stops being a column.
const TOP_N = 12;
const BAR_H = 10;
// A count of 1 next to a count of 6.129 still has to be visible as a mark.
const MIN_BAR_PCT = 1.5;

export const BreakdownChart: React.FC<{
  title: string; data: BreakdownItem[]; maxBars?: number; periodo?: PeriodoProps;
  // Required, not optional: a chart that forgets either reaches the download
  // without saying what it shows or whose calls they are — and an optional prop
  // lets that happen in silence. It did, once.
  description: string; comune: string; edit?: TextEdit;
}> = ({ title, data, maxBars = TOP_N, periodo, description, comune, edit }) => {
  const shown = data.slice(0, maxBars);
  const hidden = data.length - shown.length;
  const max = shown.reduce((m, d) => Math.max(m, d.count), 0);

  return (
    // Over all rows, the ones past TOP_N included: the total is of the calls,
    // not of the bars that fit.
    <ChartCard title={title} description={description} comune={comune} periodo={periodo} edit={edit}
      total={sumCounts(data)}>
      <Box
        sx={{
          display: 'grid',
          // The name column gives way first when the card is narrow; the bar keeps
          // a floor so it never collapses to nothing, and the figures never move.
          gridTemplateColumns: 'minmax(6rem, 14rem) minmax(60px, 1fr) auto',
          alignItems: 'center',
          columnGap: 2,
          rowGap: 1.25,
        }}
      >
        {shown.map(d => (
          <React.Fragment key={d.value}>
            <Typography
              variant="body2"
              title={d.value}
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {d.value}
            </Typography>
            <Box aria-hidden sx={{ height: BAR_H, bgcolor: 'action.hover', borderRadius: '2px' }}>
              <Box
                sx={{
                  height: '100%',
                  borderRadius: '2px',
                  bgcolor: CHART_COLOR,
                  width: `${max ? Math.max((d.count / max) * 100, MIN_BAR_PCT) : 0}%`,
                }}
              />
            </Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}
            >
              {d.count > 0 ? fmtInt(d.count) : ''}
            </Typography>
          </React.Fragment>
        ))}
      </Box>
      {hidden > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
          + {hidden} altri non mostrati
        </Typography>
      )}
    </ChartCard>
  );
};

// -- Heatmap: plain MUI 7×24 grid, no chart lib -----------------------------
const DAYS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
export const HeatmapGrid: React.FC<{
  data: HeatmapCell[]; periodo?: PeriodoProps; comune: string; title: string; description: string; edit?: TextEdit;
}> = ({ data, periodo, comune, title, description, edit }) => {
  const matrix = Array.from({ length: 7 }, () => Array(24).fill(0));
  let max = 0;
  data.forEach(c => {
    matrix[c.weekday][c.hour] = c.count;
    if (c.count > max) max = c.count;
  });
  const color = (n: number) =>
    n === 0 ? 'transparent' : `rgba(${CHART_RGB}, ${0.12 + 0.88 * (n / max)})`;

  return (
    <ChartCard title={title} description={description} comune={comune} periodo={periodo} edit={edit}
      total={sumCounts(data)}>
      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: `40px repeat(24, 1fr)`, gap: '2px', minWidth: 640 }}>
          <Box />
          {Array.from({ length: 24 }, (_v, h) => (
            <Typography key={h} variant="caption" align="center" color="text.secondary">{h}</Typography>
          ))}
          {matrix.map((row, di) => (
            <React.Fragment key={di}>
              <Typography variant="caption" color="text.secondary" sx={{ pr: 0.5 }}>{DAYS[di]}</Typography>
              {row.map((n, hi) => (
                <Tooltip key={hi} title={`${DAYS[di]} ${hi}:00 — ${n}`}>
                  <Box sx={{
                    height: 20, borderRadius: '2px', bgcolor: color(n),
                    border: '1px solid', borderColor: 'divider',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 9, lineHeight: 1,
                    color: max && n / max > 0.55 ? '#fff' : 'rgba(0,0,0,0.7)',
                  }}>
                    {n > 0 ? fmtInt(n) : ''}
                  </Box>
                </Tooltip>
              ))}
            </React.Fragment>
          ))}
        </Box>
      </Box>
    </ChartCard>
  );
};
